import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { removeSecondaryInsuranceFromDetails, upsertInsurance } from './utils';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IOnboardingQuestionnaireInsurance } from '../../../../../pages/questionnaires/onboarding/components/forms/insurance/types';
import type { OnboardingQuestionnaireState } from '../../types';
import type {
  DeleteOnboardingQuestionnaireSecondaryInsurancePayload,
  OnboardingInsuranceThunks,
  SetOnboardingQuestionnaireNoSecondaryInsurancePayload,
} from './types';

export function addOnboardingInsuranceCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingInsuranceThunks,
) {
  addUpsertOnboardingQuestionnairePrimaryInsuranceCases(builder, thunks);
  addUpsertOnboardingQuestionnaireSecondaryInsuranceCases(builder, thunks);
  addDeleteOnboardingQuestionnaireSecondaryInsuranceCases(builder, thunks);
  addSetOnboardingQuestionnaireNoSecondaryInsuranceCases(builder, thunks);
}

function addUpsertOnboardingQuestionnairePrimaryInsuranceCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingInsuranceThunks,
) {
  const { upsertOnboardingQuestionnairePrimaryInsurance } = thunks;

  builder
    .addCase(
      upsertOnboardingQuestionnairePrimaryInsurance.pending,
      (state: OnboardingQuestionnaireState) => {
        state.upsertPrimaryInsuranceLoading = true;
        state.error = null;
      },
    )
    .addCase(
      upsertOnboardingQuestionnairePrimaryInsurance.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireInsurance>,
      ) => {
        state.upsertPrimaryInsuranceLoading = false;
        upsertInsurance(state, action.payload);
      },
    )
    .addCase(
      upsertOnboardingQuestionnairePrimaryInsurance.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.upsertPrimaryInsuranceLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addUpsertOnboardingQuestionnaireSecondaryInsuranceCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingInsuranceThunks,
) {
  const { upsertOnboardingQuestionnaireSecondaryInsurance } = thunks;

  builder
    .addCase(
      upsertOnboardingQuestionnaireSecondaryInsurance.pending,
      (state: OnboardingQuestionnaireState) => {
        state.upsertSecondaryInsuranceLoading = true;
        state.error = null;
      },
    )
    .addCase(
      upsertOnboardingQuestionnaireSecondaryInsurance.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireInsurance>,
      ) => {
        state.upsertSecondaryInsuranceLoading = false;
        upsertInsurance(state, action.payload);
      },
    )
    .addCase(
      upsertOnboardingQuestionnaireSecondaryInsurance.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.upsertSecondaryInsuranceLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addDeleteOnboardingQuestionnaireSecondaryInsuranceCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingInsuranceThunks,
) {
  const { deleteOnboardingQuestionnaireSecondaryInsurance } = thunks;

  builder
    .addCase(
      deleteOnboardingQuestionnaireSecondaryInsurance.pending,
      (state: OnboardingQuestionnaireState) => {
        state.deleteSecondaryInsuranceLoading = true;
        state.error = null;
      },
    )
    .addCase(
      deleteOnboardingQuestionnaireSecondaryInsurance.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<
          void,
          string,
          { arg: DeleteOnboardingQuestionnaireSecondaryInsurancePayload }
        >,
      ) => {
        state.deleteSecondaryInsuranceLoading = false;
        if (
          !state.onboardingQuestionnaireDetails ||
          String(state.onboardingQuestionnaireDetails.id) !==
            action.meta.arg.onboardingQuestionnaireId
        ) {
          return;
        }
        removeSecondaryInsuranceFromDetails(state);
      },
    )
    .addCase(
      deleteOnboardingQuestionnaireSecondaryInsurance.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.deleteSecondaryInsuranceLoading = false;
        toast.error(getSerializedErrorMessage(action.error), { toastId: 'global-toast-error' });
      },
    );
}

function addSetOnboardingQuestionnaireNoSecondaryInsuranceCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingInsuranceThunks,
) {
  const { setOnboardingQuestionnaireNoSecondaryInsurance } = thunks;

  builder
    .addCase(
      setOnboardingQuestionnaireNoSecondaryInsurance.pending,
      (state: OnboardingQuestionnaireState) => {
        state.noSecondaryInsuranceLoading = true;
        state.error = null;
      },
    )
    .addCase(
      setOnboardingQuestionnaireNoSecondaryInsurance.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<
          void,
          string,
          { arg: SetOnboardingQuestionnaireNoSecondaryInsurancePayload }
        >,
      ) => {
        state.noSecondaryInsuranceLoading = false;
        if (
          !state.onboardingQuestionnaireDetails ||
          String(state.onboardingQuestionnaireDetails.id) !==
            action.meta.arg.onboardingQuestionnaireId
        ) {
          return;
        }
        state.onboardingQuestionnaireDetails.no_secondary_insurance =
          action.meta.arg.noSecondaryInsurance;
      },
    )
    .addCase(
      setOnboardingQuestionnaireNoSecondaryInsurance.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.noSecondaryInsuranceLoading = false;
        toast.error(getSerializedErrorMessage(action.error), { toastId: 'global-toast-error' });
      },
    );
}
